import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Empty, Grid, Space, Spin, message, theme } from 'antd';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ProjectOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { PageHeader } from 'components/headings';
import { useUser } from 'providers/Users/UserProvider';
import { useOrderGantt } from 'hooks/useOrderGantt';
import { ImportExportToolbar } from 'components/global/ImportExport';
import { ORDEN_TAREA_COLUMNS } from 'components/global/ImportExport/ordenTareaColumns';
import EncenderGanttModal from './EncenderGanttModal';
import NuevaTareaModal from './NuevaTareaModal';
import GanttChart from './GanttChart';
import GanttMobileList from './GanttMobileList';
import ExportarGanttPdfButton from './ExportarGanttPdfButton';
import { useAyuda } from 'components/ayuda';

// Página de la carta Gantt de una ODT (ruta /admin/odt/:idOrder/gantt). Una sola
// rejilla: la lista de tareas editable (izquierda, fija) y las barras en el tiempo
// (derecha). Si el modo está apagado, muestra una invitación a armarla.
export default function ODTGanttPage() {
  const { idOrder } = useParams();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  // `undefined` (primer render) cuenta como escritorio: evita el parpadeo en pantallas anchas.
  const esMovil = screens.lg === false;
  const userCtx = useUser();
  const especialistas = userCtx?.especialistas ?? [];

  const {
    order,
    tareas,
    dependencias,
    loading,
    error,
    encender,
    apagar,
    actualizarItem,
    reordenar,
    crearItem,
    recargar,
  } = useOrderGantt(idOrder);

  const [modalOpen, setModalOpen] = useState(false);
  const [encendiendo, setEncendiendo] = useState(false);
  // null = cerrado. { padre: null } = tarea de primer nivel.
  // { padre: <tarea> } = subtarea de esa fila.
  const [nuevaTarea, setNuevaTarea] = useState(null);
  const [creando, setCreando] = useState(false);
  const { abrir: abrirAyuda } = useAyuda();

  useEffect(() => {
    if (userCtx && !userCtx.hasCalled && !userCtx.isLoading) userCtx.call?.();
  }, [userCtx]);

  const readOnly = Boolean(order?.completed_at);
  const modoGantt = Boolean(order?.modo_gantt);
  const nombreCliente = order?.budget?.cliente?.name;

  const ieConfig = {
    exportUrl: (_scope, format) => `/orders/${idOrder}/tareas/export?format=${format}`,
    validateUrl: `/orders/${idOrder}/tareas/import/validate`,
    commitUrl: `/orders/${idOrder}/tareas/import/commit`,
  };

  const handleEncender = async (fecha) => {
    setEncendiendo(true);
    const res = await encender(fecha);
    setEncendiendo(false);
    if (res?.codeMessage) {
      message.error(res.message || 'No se pudo armar la carta Gantt');
      return;
    }
    setModalOpen(false);
  };

  const handleApagar = async () => {
    const res = await apagar();
    if (res?.codeMessage) message.error(res.message || 'No se pudo volver al modo simple');
  };

  const handleAgregarSubtarea = (idPadre) => {
    const padre = tareas.find((t) => t.id === idPadre) ?? null;
    setNuevaTarea({ padre });
  };

  const handleCrearTarea = async (payload) => {
    // Guarda contra doble envío: si ya hay una creación en curso, ignora el
    // reintento en vez de disparar una segunda llamada mientras la primera
    // todavía no termina.
    if (creando) return;
    setCreando(true);
    let res;
    try {
      res = await crearItem(payload);
    } finally {
      // El cliente de la API devuelve el error en el resultado en vez de
      // lanzarlo, pero eso es una garantía de afuera: si alguna vez dejara de
      // cumplirse, sin este bloque el botón quedaría trabado en "creando" para
      // siempre, sin aviso y sin forma de reintentar salvo recargar la página.
      setCreando(false);
    }
    if (res?.codeMessage) {
      message.error(res.message || 'No se pudo crear la tarea');
      return;
    }
    setNuevaTarea(null);
  };

  const abrirTutorial = () => abrirAyuda({ tutorialId: 'gantt' });

  const acciones = (
    <Space>
      <Button icon={<PlayCircleOutlined />} onClick={abrirTutorial}>
        Ver tutorial
      </Button>
      {modoGantt ? (
        <ExportarGanttPdfButton
          idOrder={idOrder}
          order={order}
          tareas={tareas}
          especialistas={especialistas}
        />
      ) : null}
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
        Volver
      </Button>
      {modoGantt && !readOnly ? (
        <Button icon={<RollbackOutlined />} onClick={handleApagar}>
          Volver al modo simple
        </Button>
      ) : null}
    </Space>
  );

  const toolbar = !readOnly ? (
    <>
      <ImportExportToolbar
        scope={{ level: 'order', idOrder: Number(idOrder) }}
        columns={ORDEN_TAREA_COLUMNS}
        config={ieConfig}
        humanName="tareas"
        onCommitSuccess={recargar}
      />
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setNuevaTarea({ padre: null })}>
        Agregar tarea
      </Button>
    </>
  ) : null;

  return (
    <div>
      <PageHeader
        title="Carta Gantt"
        subtitle={
          nombreCliente
            ? `Planificación de la ODT · ${nombreCliente}`
            : 'Planificación de la orden de trabajo'
        }
        icon={ProjectOutlined}
        actions={acciones}
      />

      {readOnly ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Esta orden de trabajo está finalizada: la planificación es solo de lectura."
        />
      ) : null}

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      {loading && !order ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !modoGantt ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ maxWidth: 420, margin: '0 auto' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Todavía no se ha armado la carta Gantt
              </div>
              <div style={{ color: token.colorTextSecondary }}>
                Al armarla, las tareas se reparten automáticamente en el tiempo entre hoy y la fecha
                comprometida, y después puedes ajustarlas arrastrando las barras.
              </div>
            </div>
          }
          style={{ padding: 48 }}
        >
          {!readOnly ? (
            <Button type="primary" icon={<ProjectOutlined />} onClick={() => setModalOpen(true)}>
              Armar carta Gantt
            </Button>
          ) : null}
        </Empty>
      ) : esMovil ? (
        <GanttMobileList
          tareas={tareas}
          dependencias={dependencias}
          fechaComprometida={order?.fecha_comprometida ?? null}
          especialistas={especialistas}
          readOnly={readOnly}
          onEditarItem={actualizarItem}
          toolbar={toolbar}
        />
      ) : (
        <GanttChart
          tareas={tareas}
          dependencias={dependencias}
          fechaComprometida={order?.fecha_comprometida ?? null}
          especialistas={especialistas}
          readOnly={readOnly}
          onEditarItem={actualizarItem}
          onReordenar={reordenar}
          onAgregarSubtarea={handleAgregarSubtarea}
          onCambiarFechas={(idItem, campos) => actualizarItem(idItem, campos)}
          toolbar={toolbar}
        />
      )}

      <EncenderGanttModal
        open={modalOpen}
        fechaComprometidaActual={order?.fecha_comprometida ?? null}
        loading={encendiendo}
        onConfirmar={handleEncender}
        onCancelar={() => setModalOpen(false)}
      />

      <NuevaTareaModal
        open={Boolean(nuevaTarea)}
        padre={nuevaTarea?.padre ?? null}
        idCliente={order?.budget?.cliente?.id ?? null}
        especialistas={especialistas}
        guardando={creando}
        onCrear={handleCrearTarea}
        onCancelar={() => setNuevaTarea(null)}
      />
    </div>
  );
}
